import { md5 } from '../utils/md5.js'
import { ProxyTransport, ProxyTransportError } from './proxyTransport.js'
const RECOVERY_PROBE = Symbol('member recovery probe')

export class ApiService {
  constructor(config, transportOptions = {}) {
    this.config = config
    this.lastRequestTime = 0
    this.requestInterval = 350;
    this.activeAbortControllers = new Set()
    this.requestGeneration = 0
    this.transport = new ProxyTransport(transportOptions)
  }

  abortActiveRequests() {
    this.requestGeneration++
    this.activeAbortControllers.forEach((controller) => controller.abort())
    this.activeAbortControllers.clear()
  }

  // 生成随机数（时间戳）
  generateRandom() {
    return Math.floor(Date.now() / 1000)
  }

  // 计算签名：md5(md5(token + ts) + datakey)
  // 与旧游戏不同：新游戏（汉将无双）不使用固定 keynum，而是每日变化的 datakey（客户端本地日期）
  calculateSignature(token, timestamp) {
    return md5(md5(token + timestamp) + this.datakey())
  }

  // 新游戏 datakey：基于客户端本地日期计算，每日变化
  datakey() {
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    const day = now.getDate()
    const week = now.getDay()
    return Math.ceil((year + day + month) * Math.pow(week + 1, week + 1))
  }

  normalizeServerUrl(server) {
    const rawServer = String(server || "").trim()
    if (!rawServer) {
      throw new Error("服务器地址不能为空")
    }

    const withProtocol = /^https?:\/\//i.test(rawServer) ? rawServer : `http://${rawServer}`
    const url = new URL(withProtocol)
    return `${url.origin}${url.pathname.replace(/\/$/, "") || ""}`.replace(/\/index\.php$/i, "")
  }

  getServerOrigin(server, port) {
    const rawServer = String(server || "").trim()
    if (!rawServer) {
      throw new Error("服务器地址不能为空")
    }

    const withProtocol = /^https?:\/\//i.test(rawServer) ? rawServer : `http://${rawServer}`
    const url = new URL(withProtocol)
    if (port !== undefined) {
      url.port = String(port)
    }
    return url.origin
  }

  // 端口映射（汉将无双）
  //  server 传入游戏入口（如 111.231.174.15:9100），按用途映射到对应端口
  //  - api      游戏 API     -> 9103
  //  - login    UC 账号中心  -> 9101
  //  - referrer 游戏入口     -> 9100
  getEndpointOrigin(server, endpoint) {
    const rawServer = String(server || "").trim()
    if (!rawServer) {
      throw new Error("服务器地址不能为空")
    }

    const withProtocol = /^https?:\/\//i.test(rawServer) ? rawServer : `http://${rawServer}`
    const url = new URL(withProtocol)

    if (endpoint === "api") {
      url.port = "9103"
    } else if (endpoint === "login") {
      url.port = "9101"
    } else if (endpoint === "referrer") {
      url.port = "9100"
    }
    return url.origin
  }

  buildApiUrl(server, query) {
    return `${this.getEndpointOrigin(server, "api")}/index.php?${query}`
  }

  buildLoginUrl(server, query) {
    return `${this.getEndpointOrigin(server, "login")}/index.php?${query}`
  }

  buildReferrer(server) {
    try {
      return `${this.getEndpointOrigin(server, "referrer")}/`
    } catch (error) {
      return this.config.referrer
    }
  }

  // 简化的API请求（无队列版本）
  async apiRequest(params, body, server, purpose, signal) {
    if (this.migrationPaused) throw new Error('迁移恢复已停止请求，请刷新页面后再执行')
    const requestGeneration = this.requestGeneration
    const abortController = new AbortController()
    const cancel = () => abortController.abort()
    signal?.addEventListener('abort', cancel, { once: true })
    if (signal?.aborted) cancel()
    this.activeAbortControllers.add(abortController)
    // 简单的请求间隔控制
    const now = Date.now()
    const timeSinceLastRequest = now - this.lastRequestTime
    const waitTime = Math.max(0, this.requestInterval - timeSinceLastRequest)

    try {
      if (abortController.signal.aborted) throw new Error('请求已被取消')
      if (waitTime > 0) {
        await new Promise((resolve, reject) => {
          const timer = setTimeout(() => {
            abortController.signal.removeEventListener("abort", onAbort)
            resolve()
          }, waitTime)
          const onAbort = () => {
            clearTimeout(timer)
            abortController.signal.removeEventListener("abort", onAbort)
            reject(new Error("请求已被取消"))
          }
          abortController.signal.addEventListener("abort", onAbort, { once: true })
        })
      }

      if (requestGeneration !== this.requestGeneration || abortController.signal.aborted) {
        throw new Error("请求已被取消")
      }

      this.lastRequestTime = Date.now()
      const result = await this._doApiRequest(params, body, server, abortController, purpose)
      return result
    } catch (error) {
      console.error(`API请求失败: ${error.message}`)
      throw error
    } finally {
      signal?.removeEventListener('abort', cancel)
      this.activeAbortControllers.delete(abortController)
    }
  }

  // 实际的API请求执行
  async _doApiRequest(params, body, server, abortController = null, purpose) {
    const rand = params.loginTimestamp || this.generateRandom()
    const token = params.token !== undefined ? params.token : ""
    const tokenUid = params.token_uid || ""
    const signature = params.signature || this.calculateSignature(token, rand)
    
    const urlParams = {
      v: params.v || "0",
      c: params.c,
      m: params.m,
      token: token,
      channel: 106,
      lang: "zh-cn",
      rand: rand,
      signature: signature,
    }

    // 新游戏按区服区分服务器（正式服 s1），游戏 API 需要携带 hjsg_server
    if (params.hjsg_server || this.config.hjsgServer) {
      urlParams.hjsg_server = params.hjsg_server || this.config.hjsgServer
    }

    if (tokenUid) {
      urlParams.token_uid = tokenUid
    }

    const reservedParams = new Set([
      "v",
      "c",
      "m",
      "token",
      "token_uid",
      "signature",
      "loginTimestamp",
      "hjsg_server",
    ])

    for (const [key, value] of Object.entries(params)) {
      if (reservedParams.has(key) || value === undefined || value === null) {
        continue
      }
      urlParams[key] = value
    }

    if (params.d) {
      urlParams.d = params.d
    }

    const query = new URLSearchParams(urlParams).toString()

    const requestController = abortController || new AbortController()
    const ownsController = !abortController
    if (ownsController) this.activeAbortControllers.add(requestController)
    try {
      const send = purpose === RECOVERY_PROBE ? this.transport.probeMember.bind(this.transport) : this.transport.request.bind(this.transport)
      const response = await send({
        endpoint: 'game', server, query, directUrl: this.buildApiUrl(server, query),
        referrer: this.buildReferrer(server),
        body: new URLSearchParams(body).toString(),
        signal: requestController.signal,
      })

      if (response.status === 403) {
        return "403"
      }

      // 先读取原始文本，再解析 JSON（避免 json() 失败后 text() 报 "Body has already been read"）
      const rawText = await response.text()
      if (rawText == "403" || rawText.indexOf("403-") !== -1) {
        return rawText
      }
      try {
        return JSON.parse(rawText)
      } catch (jsonError) {
        console.warn('游戏接口返回非 JSON 响应')
        return null
      }
    } catch (error) {
      if (error instanceof ProxyTransportError) throw error
      if (requestController.signal.aborted) {
        throw new Error("请求已被取消")
      }
      if (purpose === RECOVERY_PROBE) throw new ProxyTransportError('PROXY_NETWORK')
      if (this.transport.mode === 'proxy') throw this.transport.fail('PROXY_NETWORK')
      console.error('游戏接口网络请求失败')
      return null
    } finally {
      if (ownsController) this.activeAbortControllers.delete(requestController)
    }
  }

  // UC 账号中心登录（9101）
  async accountLogin(server, username, password) {
    if (this.migrationPaused) throw new Error('迁移恢复已停止请求，请刷新页面后再登录')
    const requestGeneration = this.requestGeneration
    const rand = this.generateRandom()
    const token = ""
    const signature = this.calculateSignature(token, rand)
    const query = `&c=user&m=login&&${new URLSearchParams({
      token,
      channel: 106,
      lang: "zh-cn",
      rand,
      signature,
    }).toString()}`

    const abortController = new AbortController()
    this.activeAbortControllers.add(abortController)
    try {
      const response = await this.transport.request({
        endpoint: 'login', server, query, directUrl: this.buildLoginUrl(server, query),
        referrer: this.buildReferrer(server),
        body: new URLSearchParams({
          u: username,
          p: password,
          mac: "00:00:00:00:00:00",
          adid: "",
          devicetoken: "000000",
          channel: 106,
          token,
        }).toString(),
        signal: abortController.signal,
      })
      if (requestGeneration !== this.requestGeneration) {
        throw new Error("请求已被取消")
      }
      const rawText = await response.text()
      try { return JSON.parse(rawText) } catch { console.warn('游戏登录接口返回非 JSON 响应'); return null }
    } catch (error) {
      if (error instanceof ProxyTransportError) throw error
      if (abortController.signal.aborted) {
        throw new Error("请求已被取消")
      }
      if (this.transport.mode === 'proxy') throw this.transport.fail('PROXY_NETWORK')
      return null
    } finally {
      this.activeAbortControllers.delete(abortController)
    }
  }

  // 登录
  async login(server, username, password) {
    const loginTimestamp = this.generateRandom()

    const accountLoginData = await this.accountLogin(server, username, password)
    if (accountLoginData && accountLoginData.status !== undefined && accountLoginData.status !== 1) {
      return {
        success: false,
        error: accountLoginData.info || accountLoginData.message || "账号登录失败"
      }
    }

    const data = await this.apiRequest(
      { v: "0", c: "login", m: "user", token: "", loginTimestamp: loginTimestamp },
      {
        mac: "00:00:00:00:00:00",
        devicetoken: "000000",
        adid: "",
        u: username,
        p: password,
        channel: 106,
        token: "",
      },
      server
    )

    if (data && data.status === 1) {
      // 获取用户信息；若偶发失败（如接口抖动/限流导致 uid 缺失），重试几次
      let userInfo = null
      const MAX_USERINFO_ATTEMPTS = 3
      for (let attempt = 0; attempt < MAX_USERINFO_ATTEMPTS; attempt++) {
        userInfo = await this.getUserInfo(server, data.token)
        if (userInfo && userInfo.uid) break
        if (attempt < MAX_USERINFO_ATTEMPTS - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }

      if (userInfo && userInfo.uid) {
        return {
          success: true,
          token: data.token,
          userInfo: userInfo
        }
      }

      // 登录成功但拿不到用户信息：视为失败，避免调用方拿到 success:true + userInfo:null 后崩溃
      return {
        success: false,
        error: "登录成功但获取用户信息失败"
      }
    } else {
      return {
        success: false,
        error: data ? data.info : "未知错误"
      }
    }
  }

  // 获取用户信息
  async getUserInfo(server, token) {
    const data = await this.apiRequest(
      {
        c: "member",
        m: "index",
        token: token,
      },
      {},
      server
    )

    if (data && data.uid) {
      return {
        uid: data.uid,
        nickname: decodeURIComponent(data.nickname || ""),
        level: data.level || 0,
        vip: data.vip || 0,
        silver: data.silver || 0,
        gold: data.gold || 0,
        act: data.act || 0,
        country: data.country || 0,
      }
    }
    return null
  }

  // 检查登录状态
  async probeConnection(account, signal) {
    const result = await this.apiRequest({ c: 'member', m: 'index', token: account.token || '',
      token_uid: account.tokenUid }, {}, account.server, RECOVERY_PROBE, signal)
    // 原始前端把 403 作为 token 失效处理；连通后由正常任务流程重登。
    if (result === '403' || (result && typeof result === 'object' && result.uid)) return true
    throw new ProxyTransportError('UPSTREAM_NETWORK')
  }

  async checkLoginStatus(server, token) {
    const data = await this.apiRequest(
      {
        c: "member",
        m: "index",
        token: token,
      },
      {},
      server
    )

    return data && data.uid
  }
}
